import React, { useEffect, useState } from 'react'
import { Button, Modal, Select } from '@surecompanies/core_components'
import { PLAN_REPORT_TYPE_OPTIONS, type PlanReportType } from '../constants'
import styles from '../PlanManagementV3.module.scss'

interface Props {
  open: boolean
  year: number | undefined
  isExporting: boolean
  downloadedBytes: number
  onClose: () => void
  onExport: (reportType: PlanReportType) => void
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function ExportPlanReportsModal({
  open,
  year,
  isExporting,
  downloadedBytes,
  onClose,
  onExport,
}: Props) {
  const [reportType, setReportType] = useState<PlanReportType | undefined>(
    undefined
  )

  useEffect(() => {
    if (open) return
    setReportType(undefined)
  }, [open])

  const handleClose = () => {
    if (isExporting) return
    onClose()
  }

  const handleExport = () => {
    if (year === undefined || reportType === undefined || isExporting) return
    onExport(reportType)
  }

  const isExportDisabled =
    year === undefined || reportType === undefined || isExporting

  return (
    <Modal
      open={open}
      title="Export Plan Reports"
      maskClosable={false}
      closable={!isExporting}
      onCancel={handleClose}
      footer={[
        <Button
          key="cancel"
          htmlType="button"
          disabled={isExporting}
          onClick={handleClose}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          htmlType="button"
          type="primary"
          disabled={isExportDisabled}
          loading={isExporting}
          onClick={handleExport}
        >
          Download
        </Button>,
      ]}
      centered
      destroyOnClose
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Year</span>
          <Select
            disabled
            value={year}
            options={
              year === undefined ? [] : [{ label: String(year), value: year }]
            }
          />
        </div>

        <div className={styles.modalField}>
          <span className={styles.modalFieldLabel}>Report</span>
          <Select
            placeholder="Select a report"
            value={reportType}
            onChange={(v) => setReportType(v)}
            options={PLAN_REPORT_TYPE_OPTIONS}
          />
        </div>

        {isExporting && (
          <span className={styles.modalFieldLabel}>
            Downloading… {formatBytes(downloadedBytes)}
          </span>
        )}
      </div>
    </Modal>
  )
}
